import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ProductItemImage from '../../Types/ProductItemImage';
import useReady from '../../Hooks/useReady';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { ResponsiveImageModel } from '../../Types/ResponsiveImageModel';
import { uniqueId } from 'lodash-es';
import { Navigation } from 'swiper';
import Icon from '../Icon';
import { isEqual } from 'lodash-es';
import 'swiper/css/effect-fade';
import useIsMobile from '../../Hooks/useIsMobile';
import clsx from 'clsx';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import useIsStoreScreen from '../../Hooks/useIsStoreScreen';

interface ProductCardImageSliderProps {
  images: ProductItemImage[];
  roomshotImage?: ResponsiveImageModel;
  patternImage?: ResponsiveImageModel;
  forceRoomshotImage?: boolean;
  forcePatternImage?: boolean;
  productUrl?: string;
  disableSlider?: boolean;
}

interface ProductCardImageProps {
  image?: ResponsiveImageModel;
  url?: string;
  pointerEventsEnabled?: boolean;
}

const ProductCardImage = ({
  image = {},
  url,
  pointerEventsEnabled: enablePointerEvents = true,
}: ProductCardImageProps) => (
  <a
    href={url}
    onClick={(e) => !enablePointerEvents && e.preventDefault()}
    tabIndex={-1}
    className={clsx(
      !enablePointerEvents && 'pointer-events-none',
      'w-full overflow-hidden rounded'
    )}
  >
    <ResponsiveImage
      {...image}
      lazyLoad={true}
      imgProps={{
        className: 'aspect-[3/4] h-full w-full object-cover',
      }}
    />
  </a>
);

const ProductCardImageSlider = ({
  images,
  roomshotImage,
  patternImage,
  productUrl,
  forceRoomshotImage = false,
  forcePatternImage = false,
  disableSlider = false,
}: ProductCardImageSliderProps) => {
  const ready = useReady();
  const uuid = useMemo(() => uniqueId(), []);
  const prevBtnRef = useRef<HTMLButtonElement | null>(null);
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = useIsMobile();

  const getInitialImage = useCallback(() => {
    if (forceRoomshotImage && roomshotImage && !forcePatternImage) {
      return roomshotImage;
    } else if (forcePatternImage && patternImage && !forceRoomshotImage) {
      return patternImage;
    } else {
      return images[0]?.imageModel;
    }
  }, [
    forcePatternImage,
    forceRoomshotImage,
    roomshotImage,
    patternImage,
    images,
  ]);

  const forcedImageIndex = useMemo(
    () =>
      images.findIndex((img) =>
        isEqual(img.imageModel, getInitialImage())
      ),
    [images, getInitialImage]
  );

  const swiperRef = useRef<SwiperRef>(null);

  const isStoreScreen = useIsStoreScreen();

  useEffect(() => {
    if (
      (forceRoomshotImage || forcePatternImage) &&
      forcedImageIndex! != -1
    ) {
      if (swiperRef.current?.swiper) {
        swiperRef.current?.swiper.slideTo(forcedImageIndex);
      }
    } else if (forcedImageIndex != -1) {
      if (swiperRef.current?.swiper) {
        swiperRef.current?.swiper.slideTo(0);
      }
    }
  }, [forceRoomshotImage, forcePatternImage, forcedImageIndex]);

  return !ready ||
    images.length < 2 ||
    isMobile ||
    isStoreScreen ||
    disableSlider ? (
    <ProductCardImage
      image={getInitialImage()}
      url={productUrl}
      pointerEventsEnabled={!isMobile && !isStoreScreen}
    />
  ) : (
    <>
      <Swiper
        slidesPerView={1}
        modules={[Navigation]}
        navigation={{
          prevEl: prevBtnRef?.current,
          nextEl: nextBtnRef?.current,
        }}
        className="!z-0"
        initialSlide={
          forceRoomshotImage || forcePatternImage ? forcedImageIndex : 0
        }
        threshold={5}
        ref={swiperRef}
        nested={true}
        enabled={!isMobile && !isStoreScreen}
      >
        {images.map((img, idx) => (
          <SwiperSlide
            key={uuid + '-product-card-image-' + idx}
            className="rounded"
          >
            <ProductCardImage
              image={img.imageModel}
              url={productUrl}
              pointerEventsEnabled={!isMobile && !isStoreScreen}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <button
        ref={prevBtnRef}
        className="duration-600 group absolute -left-1 top-[calc(50%-48px)] z-10 hidden translate-y-[50%] p-2 transition-all group-hover:left-0 focus:left-0 focus-visible:left-0 disabled:opacity-60 lg:flex"
      >
        <div className=" duration-600 pointer-events-none h-6 w-6 items-center justify-center rounded-full bg-opacity-white-90 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus:pointer-events-auto group-focus:opacity-100 lg:flex lg:h-8 lg:w-8">
          <Icon name="chevron-left" className="h-4 w-4" />
        </div>
      </button>
      <button
        ref={nextBtnRef}
        className="duration-600 group absolute -right-1 top-[calc(50%-48px)] z-10 hidden translate-y-[50%] p-2 transition-all group-hover:right-0 focus:right-0 focus-visible:right-0 disabled:opacity-60  lg:flex"
      >
        <div className="duration-600 pointer-events-none h-6 w-6 items-center justify-center rounded-full bg-opacity-white-90 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus:pointer-events-auto group-focus:opacity-100 lg:flex lg:h-8 lg:w-8">
          <Icon name="chevron-right" className="h-4 w-4" />
        </div>
      </button>
    </>
  );
};

export default ProductCardImageSlider;
